import { useEffect, useMemo, useState } from "react";
import { CreditCard, Loader2, LockKeyhole, LogOut, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import {
  BILLING_CYCLES,
  BILLING_CYCLE_LABELS,
  isStripeTestMode,
  planChargeForCycle,
  plans,
  type BillingCycle,
  type PlanConfig,
} from "@/config/stripePlans";

type SelfServePlanKey = "builder" | "starter" | "growing" | "premium";

const SELF_SERVE_PLAN_KEYS: SelfServePlanKey[] = [
  "builder",
  "starter",
  "growing",
  "premium",
];

interface TrialExpiredPaymentGateProps {
  onRefreshSubscription: () => Promise<void>;
  onRefreshBusinessContext: () => Promise<void>;
  onSignOut: () => Promise<void>;
}

// "S$540 billed quarterly". Sandbox is monthly-only (its quarterly and yearly
// entries reuse the monthly price), so it always says monthly.
function billingNote(plan: PlanConfig, cycle: BillingCycle): string {
  const charge = planChargeForCycle(plan, cycle);
  if (cycle === "monthly" || charge === null || plan.stripe?.[cycle]?.priceId === plan.stripe?.monthly.priceId) {
    return "Billed monthly";
  }
  return `S$${charge.toLocaleString()} billed ${cycle}`;
}

function checkoutReturnUrl(result: "success" | "cancel"): string {
  const url = new URL(window.location.origin);
  const currentParams = new URLSearchParams(window.location.search);
  if (currentParams.get("subdomain") === "admin") {
    url.searchParams.set("subdomain", "admin");
  }
  url.searchParams.set("tab", "home");
  url.searchParams.set("checkout", result);
  return url.toString();
}

export default function TrialExpiredPaymentGate({
  onRefreshSubscription,
  onRefreshBusinessContext,
  onSignOut,
}: TrialExpiredPaymentGateProps) {
  const [billingCycle, setBillingCycle] = useState<BillingCycle>("monthly");
  const [checkoutPlan, setCheckoutPlan] = useState<SelfServePlanKey | null>(null);
  const [error, setError] = useState<string | null>(null);
  const checkoutResult = useMemo(
    () => new URLSearchParams(window.location.search).get("checkout"),
    [],
  );
  const [confirmingPayment, setConfirmingPayment] = useState(checkoutResult === "success");

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    if (checkoutResult !== "success") return;

    let cancelled = false;
    let attempts = 0;
    let timeoutId: number | null = null;
    const refreshUntilWebhookSettles = async () => {
      if (cancelled) return;
      attempts += 1;
      await Promise.all([onRefreshSubscription(), onRefreshBusinessContext()]);
      if (!cancelled && attempts < 10) {
        timeoutId = window.setTimeout(refreshUntilWebhookSettles, 2_000);
      } else if (!cancelled) {
        setConfirmingPayment(false);
      }
    };

    void refreshUntilWebhookSettles();
    return () => {
      cancelled = true;
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
  }, [checkoutResult, onRefreshBusinessContext, onRefreshSubscription]);

  const startCheckout = async (planKey: SelfServePlanKey) => {
    const plan = plans[planKey];
    const stripePrice = plan?.stripe?.[billingCycle];
    if (!stripePrice) {
      setError("This plan is not available for online checkout yet.");
      return;
    }

    setCheckoutPlan(planKey);
    setError(null);
    try {
      const { data, error: checkoutError } = await supabase.functions.invoke("create-checkout", {
        body: {
          priceId: stripePrice.priceId,
          test_mode: isStripeTestMode,
          successUrl: checkoutReturnUrl("success"),
          returnUrl: checkoutReturnUrl("cancel"),
          metadata: { source: "premium_trial_expired_gate" },
        },
      });
      if (checkoutError) throw checkoutError;
      if (!data?.url) throw new Error("Stripe did not return a checkout URL.");
      window.location.assign(data.url);
    } catch (checkoutError) {
      setError(
        checkoutError instanceof Error
          ? checkoutError.message
          : "Checkout could not be started. Please try again.",
      );
      setCheckoutPlan(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-slate-950/55 p-4 backdrop-blur-md"
      role="dialog"
      aria-modal="true"
      aria-labelledby="trial-expired-title"
      aria-describedby="trial-expired-description"
      data-testid="trial-expired-payment-gate"
    >
      <div className="my-auto w-full max-w-5xl rounded-2xl border border-border bg-background p-5 shadow-2xl sm:p-8">
        <div className="mx-auto max-w-2xl text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <LockKeyhole className="h-6 w-6" aria-hidden="true" />
          </div>
          <h1 id="trial-expired-title" className="text-2xl font-semibold text-foreground sm:text-3xl">
            Your 14-day Premium trial has ended
          </h1>
          <p id="trial-expired-description" className="mt-2 text-sm text-muted-foreground sm:text-base">
            Choose a plan to continue using Dibs. Your data remains safely stored while access is paused.
          </p>
          <div className="mx-auto mt-5 inline-flex rounded-lg border border-border bg-muted/40 p-1" aria-label="Billing cycle">
            {BILLING_CYCLES.map((cycle) => (
              <Button
                key={cycle}
                type="button"
                size="sm"
                variant={billingCycle === cycle ? "default" : "ghost"}
                aria-pressed={billingCycle === cycle}
                onClick={() => setBillingCycle(cycle)}
              >
                {BILLING_CYCLE_LABELS[cycle]}
              </Button>
            ))}
          </div>
        </div>

        {confirmingPayment && (
          <div className="mx-auto mt-5 flex max-w-xl items-center justify-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Confirming your subscription with Stripe…
          </div>
        )}
        {checkoutResult === "cancel" && (
          <p className="mx-auto mt-5 max-w-xl rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-center text-sm text-amber-900">
            Checkout was cancelled. Choose a plan whenever you are ready.
          </p>
        )}
        {error && (
          <p role="alert" className="mx-auto mt-5 max-w-xl rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-center text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="mt-7 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {SELF_SERVE_PLAN_KEYS.map((planKey) => {
            const plan = plans[planKey];
            const isLoading = checkoutPlan === planKey;
            return (
              <article key={planKey} className="flex min-h-52 flex-col rounded-xl border border-border bg-card p-4">
                <h2 className="text-lg font-semibold text-card-foreground">{plan.name}</h2>
                <p className="mt-1 line-clamp-3 text-xs text-muted-foreground">{plan.description}</p>
                <p className="mt-4 text-2xl font-semibold text-foreground">
                  S${plan.price[billingCycle] ?? plan.price.monthly}
                  <span className="text-xs font-normal text-muted-foreground">/mo</span>
                </p>
                <p className="text-xs text-muted-foreground">{billingNote(plan, billingCycle)}</p>
                <Button
                  className="mt-auto w-full"
                  type="button"
                  variant={planKey === "premium" ? "default" : "outline"}
                  disabled={checkoutPlan !== null || confirmingPayment}
                  onClick={() => void startCheckout(planKey)}
                >
                  {isLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <CreditCard className="mr-2 h-4 w-4" aria-hidden="true" />
                  )}
                  Choose {plan.name}
                </Button>
              </article>
            );
          })}
        </div>

        <div className="mt-6 flex flex-col items-center justify-between gap-3 border-t border-border pt-5 sm:flex-row">
          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              Secure checkout powered by Stripe. Your trial was never charged automatically.
              {isStripeTestMode && " Test mode is active; no real money will be charged."}
            </span>
          </p>
          <Button type="button" variant="ghost" onClick={() => void onSignOut()}>
            <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}
